import { MdCheckCircleOutline, MdWarningAmber } from 'react-icons/md'
import { css, keyframes } from '@emotion/react'

import ApiManager from '../../api/ApiManager'
import DemographicsForm from './DemographicsForm'
import FormUtils from '../../utils/FormUtils'
import { HttpMethods } from '../../enums/HttpMethodsEnum'
import ModalComponent from '../common/Modal/Modal.component'
import React from 'react'
import UrlConstants from '../../constants/UrlConstants'
import customerStore from '../../datastore/CustomerStore'
import { getPlanYear } from '../../utils/CommonUtils'
import { observer } from 'mobx-react'
import { pollQuotesPullStatus } from '../../utils/quotesPullStatus'
import snackbarStore from '../../datastore/SnackbarStore'
import styled from '@emotion/styled'
import theme from '../../global/theme'

export const SAMPLE_PRICE_BANNER_KEY = 'eip_sample_prices_banner'

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(34,44,105,0.4), 0 8px 32px rgba(34,44,105,0.22); }
  60%  { box-shadow: 0 0 0 10px rgba(34,44,105,0), 0 8px 32px rgba(34,44,105,0.22); }
  100% { box-shadow: 0 0 0 0 rgba(34,44,105,0), 0 8px 32px rgba(34,44,105,0.22); }
`

const pulsingStyle = css`
  animation: ${pulse} 2s ease-out infinite;
`

const Banner = styled.div<{ confirmed?: boolean }>`
  position: sticky;
  top: 95px;
  z-index: 1;
  width: 100%;
  border-radius: 16px;
  background: ${({ confirmed }) =>
    confirmed
      ? `linear-gradient(135deg, ${theme.colors.primary} 0%, #3a4baa 100%)`
      : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`};
  padding: 24px 28px;
  margin: 16px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  box-shadow: 0 8px 32px rgba(34, 44, 105, 0.22);
  ${({ confirmed }) => !confirmed && pulsingStyle}

  @media screen and (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    gap: 16px;
  }

  @media screen and (max-width: 600px) {
    top: 85px;
  }
`

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex: 1;
  min-width: 0;
`

const IconCircle = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #fff;
    font-size: 24px;
  }

  @media screen and (max-width: 700px) {
    width: 42px;
    height: 42px;
    svg {
      font-size: 20px;
    }
  }
`

const TextBlock = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 500px;

  h3 {
    margin: 0 0 5px;
    font-size: 16px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.2px;
    line-height: 1.4;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.55;
    font-weight: 400;
  }

  @media screen and (max-width: 700px) {
    h3 {
      font-size: 15px;
    }
  }
`

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  @media screen and (max-width: 700px) {
    width: 100%;
  }
`

const LooksGoodBtn = styled.button`
  background: #fff;
  color: ${theme.colors.primary};
  border: none;
  border-radius: 50px;
  padding: 11px 22px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  transition:
    transform 0.15s,
    box-shadow 0.15s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);
  }

  @media screen and (max-width: 700px) {
    flex: 1;
  }
`

const UpdateBtn = styled.button`
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  border-radius: 50px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  @media screen and (max-width: 700px) {
    flex: 1;
  }
`

const FormWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
  align-items: stretch;
`

const SamplePriceBanner: React.FC = () => {
  const [visible] = React.useState(() => {
    const v = localStorage.getItem(SAMPLE_PRICE_BANNER_KEY)
    return v === 'true' || v === 'confirmed'
  })
  const [confirmed, setConfirmed] = React.useState(
    () => localStorage.getItem(SAMPLE_PRICE_BANNER_KEY) === 'confirmed',
  )
  const [showModal, setShowModal] = React.useState(false)
  const [looksGoodLoading, setLooksGoodLoading] = React.useState(false)

  const handleLooksGood = async () => {
    setLooksGoodLoading(true)

    const payload: Record<string, string> = {}
    if (customerStore.postalCode) payload.postalCode = customerStore.postalCode
    if (customerStore.state) payload.state = customerStore.state
    if (customerStore.city) payload.city = customerStore.city
    if (customerStore.county) payload.county = customerStore.county
    if (customerStore.countyFips) payload.countyFips = customerStore.countyFips
    if (customerStore.gender) payload.gender = customerStore.gender
    if (customerStore.dateOfBirth)
      payload.dateOfBirth = customerStore.dateOfBirth.slice(0, 10)

    try {
      const response = await ApiManager.makeApiCallWithAuthentication(
        UrlConstants.UPDATE_CUSTOMER_DETAILS.USECASE,
        HttpMethods.PATCH_METHOD,
        payload,
      )
      if (response?.status === 200) {
        customerStore.setQuotePulledStatusMA(true)
        customerStore.setQuotePulledStatusMS(true)
        customerStore.setQuotePulledStatusMPartD(true)
        localStorage.setItem(SAMPLE_PRICE_BANNER_KEY, 'confirmed')
        window.dispatchEvent(new Event('eip-demographics-updated'))
        snackbarStore.set({
          snackbarOpen: true,
          snackbarMessage: 'Profile confirmed. Refreshing plans...',
          snackbarType: 'success',
        })
        pollQuotesPullStatus(getPlanYear(), true)
        setConfirmed(true)
      } else {
        throw new Error('Non-200 response')
      }
    } catch {
      snackbarStore.set({
        snackbarOpen: true,
        snackbarMessage: 'Something went wrong. Please try again.',
        snackbarType: 'error',
      })
    } finally {
      setLooksGoodLoading(false)
    }
  }

  React.useEffect(() => {
    const onDemographicsUpdated = () => {
      localStorage.setItem(SAMPLE_PRICE_BANNER_KEY, 'confirmed')
      setConfirmed(true)
    }
    window.addEventListener('eip-demographics-updated', onDemographicsUpdated)
    return () =>
      window.removeEventListener(
        'eip-demographics-updated',
        onDemographicsUpdated,
      )
  }, [])

  const quotesRefreshing =
    customerStore.isMAQuotesProcessing ||
    customerStore.isMSQuotesProcessing ||
    customerStore.isMPartDQuotesProcessing

  if (!visible || quotesRefreshing) return null

  const age = customerStore.dateOfBirth
    ? FormUtils.getAge(customerStore.dateOfBirth)
    : null
  const gender = customerStore.gender || ''

  const sampleDescriptor =
    age && gender
      ? `for a ${age}-year-old ${gender}`
      : age
        ? `for a ${age}-year-old`
        : gender
          ? `for a ${gender}`
          : ''

  const confirmedPerson = [age ? `${age} year old` : null, gender || null]
    .filter(Boolean)
    .join(' ')
  const confirmedLocation = [
    customerStore.postalCode,
    customerStore.city,
    customerStore.state,
    customerStore.county,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <>
      {showModal && (
        <ModalComponent
          setOpen={() => setShowModal(false)}
          description='Update demographics'
          width={480}
          zIndex={10001}
        >
          <FormWrapper>
            <DemographicsForm
              heading='Update your details'
              subheaderText='Enter your details to see plans and pricing curated for you.'
              onSuccess={() => setShowModal(false)}
            />
          </FormWrapper>
        </ModalComponent>
      )}
      <Banner confirmed={confirmed}>
        <ContentRow>
          <IconCircle>
            {confirmed ? <MdCheckCircleOutline /> : <MdWarningAmber />}
          </IconCircle>
          <TextBlock>
            <h3>
              {confirmed
                ? `Showing plans for ${confirmedPerson}${confirmedLocation ? ` in ${confirmedLocation}` : ''}.`
                : `These are sample prices${sampleDescriptor ? ` ${sampleDescriptor}` : ''}.`}
            </h3>
            <p>
              {confirmed
                ? 'Prices are now personalised for you based on your details.'
                : 'Prices vary by age, ZIP & gender. Confirm your details to unlock plans and pricing personalised just for you.'}
            </p>
          </TextBlock>
        </ContentRow>
        {!confirmed && (
          <ButtonGroup>
            <LooksGoodBtn
              id='looksGoodSamplePriceBanner'
              type='button'
              onClick={handleLooksGood}
              disabled={looksGoodLoading}
            >
              {looksGoodLoading ? 'Confirming...' : 'Looks good'}
            </LooksGoodBtn>
            <UpdateBtn
              id='updateMyDetailsSamplePriceBanner'
              type='button'
              onClick={() => setShowModal(true)}
            >
              Update my details
            </UpdateBtn>
          </ButtonGroup>
        )}
      </Banner>
    </>
  )
}

export default observer(SamplePriceBanner)
